import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  Check,
  Clock,
  Lock,
  X,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View, type TextStyle, type ViewStyle } from 'react-native';

import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { openWebUrl } from '@/lib/openWebUrl';
import { formatRelativeNoWrap } from '@/lib/relativeTime';
import type { DealCta } from '@/types/deal';

/**
 * Visual state for a step row. Driven by `DealStepper` from the
 * server's truth (action.step_completions, action.active_step_key,
 * action.status, step.complete_on_event); Step itself is dumb.
 *
 * Note: `Waiting` (not `Pending`) — distinct from `ACTION_STATUS.Pending`
 * which means "server received conversion signal." This state means
 * "user's current step is webhook-driven, no manual action available."
 *
 * - `Completed`: done, marked by the user (or inferred from a later tap).
 *   Deep-green check; neutral grey pill "Marked done · 4m ago". The "you
 *   said so" state of Deal Steps v2: only the brand's word gets a color.
 * - `Confirmed`: done AND the provider's postback confirmed it. Electric
 *   green check; caption "Confirmed by SoFi · 4m ago". The green
 *   "SoFi said so" state.
 * - `Active`: the user's current step AND there's an action they can
 *   take. Hollow ring with the position number, prominent CTA below.
 * - `Waiting`: the user's current step but it's webhook-driven. The
 *   user can't tap anything; they're waiting on the server. Renders
 *   as a clock icon to communicate "processing."
 * - `Locked`: a future step the user can't act on yet.
 * - `Failed`: terminal failure (rejected/expired) — rendered alone,
 *   replacing the stepper.
 */
export const STEP_STATE = {
  Completed: 'completed',
  Confirmed: 'confirmed',
  Reported: 'reported',
  Overdue: 'overdue',
  Active: 'active',
  Waiting: 'waiting',
  Locked: 'locked',
  Failed: 'failed',
} as const;
export type StepState = (typeof STEP_STATE)[keyof typeof STEP_STATE];

export type StepProps = {
  /** 1-based position (rendered inside the active bullet). */
  position: number;
  name: string;
  description: string;
  state: StepState;
  /**
   * Data-driven buttons (server source of truth). When present the step
   * renders these and ignores the legacy ctaLabel/linkUrl/linkLabel below.
   * `open_link` ctas open their url; every other cta fires `onCtaPress` with
   * itself, so the screen can route by `cta.action` (advance, voltr_*).
   */
  ctas?: DealCta[];
  ctaLabel?: string;
  onCtaPress?: (cta?: DealCta) => void;
  ctaLoading?: boolean;
  /** Reference link rendered inline below the description, e.g. "Click here". */
  linkUrl?: string;
  linkLabel?: string;
  /**
   * Optional emphasis text rendered as a warning-styled (yellow) box
   * below the description. Shown only when the description is visible.
   */
  callout?: string;
  /**
   * Deal Steps v2. Copy shown instead of `description` while this step is
   * Waiting (the "we're checking with SoFi" text on the Claim step).
   */
  waitingDescription?: string;
  /**
   * Deal Steps v2. Server-authored provenance caption for a done step, e.g.
   * "Confirmed by SoFi" or "Marked done"; the client only appends the time.
   */
  provenanceLabel?: string;
  provenanceAt?: string;
  /**
   * When set, `provenanceAt` is not the pill's own time (a reported step: the
   * user's tap, "You marked it done"), so the pill holds the status alone and
   * a caption under it reads "You marked it done 49m ago". Without the label
   * "We're checking this · 49m ago" read as when we last checked.
   */
  provenanceAtLabel?: string;
  /** Hide the connector below this row (last step). */
  isLast?: boolean;
  testID?: string;
};

export function Step({
  position,
  name,
  description,
  state,
  ctas,
  ctaLabel,
  onCtaPress,
  ctaLoading,
  linkUrl,
  linkLabel,
  callout,
  waitingDescription,
  provenanceLabel,
  provenanceAt,
  provenanceAtLabel,
  isLast = false,
  testID,
}: StepProps) {
  const hasCtas = Array.isArray(ctas) && ctas.length > 0;
  const provenanceTime = provenanceAt ? formatRelativeNoWrap(provenanceAt) : '';
  // One bubble, one Text. Short pills stay on one line ("SoFi confirmed ·
  // 4m ago"). A long one ("Double-check this step · You marked it done 3d
  // ago (Sep 7)") may wrap, but only after the "·": the time segment is
  // joined with non-breaking spaces so it never splits mid-phrase, and the
  // icon pins to the first line so the wrapped bubble still reads as one.
  const timeSegment =
    provenanceTime && provenanceAtLabel
      ? `${provenanceAtLabel.replace(/ /g, '\u00A0')}\u00A0${provenanceTime}`
      : provenanceTime;
  // Active and Failed always show the description. Everything else, Waiting
  // included, is compact by default with a "View step" toggle: the status
  // card above the stepper already carries the waiting copy, so the step
  // shows it once more only on request.
  const alwaysExpanded = state === STEP_STATE.Active || state === STEP_STATE.Failed;
  const isDone = state === STEP_STATE.Completed || state === STEP_STATE.Confirmed;
  // The user's own report: an orange clock (never a check) until the provider
  // confirms; red once the confirmation is overdue. Both carry a pill.
  const isReported = state === STEP_STATE.Reported;
  const isOverdue = state === STEP_STATE.Overdue;
  const hasPill = isDone || isReported || isOverdue;
  // A provider-only step reads its waiting copy when the deal authored one,
  // whether it is still Waiting or has gone Overdue (its `description` is the
  // paid copy, wrong under a red pill). Tapped steps never carry one.
  const shownDescription =
    (state === STEP_STATE.Waiting || isOverdue) && waitingDescription
      ? waitingDescription
      : description;
  const [expanded, setExpanded] = useState(false);
  // Reset the local expanded state whenever the step transitions to a
  // new state. Without this, a user who expanded a Locked step and then
  // saw it transition to Completed would keep it expanded — local
  // useState survives prop changes by default.
  useEffect(() => setExpanded(false), [state]);
  const showDescription = alwaysExpanded || expanded;

  return (
    <View style={styles.row} testID={testID}>
      <View style={styles.bulletColumn}>
        <View
          style={[styles.bullet, BULLET_STYLE[state]]}
          accessibilityLabel={STATE_LABEL[state]}
          testID={testID ? `${testID}-bullet-${state}` : undefined}
        >
          {isDone && <Check size={16} color={colors.frost} strokeWidth={2.5} />}
          {state === STEP_STATE.Active && (
            <Text variant="captionBold" color="deepGreen">
              {position}
            </Text>
          )}
          {(state === STEP_STATE.Waiting || isReported) && (
            <Clock size={16} color={colors.frost} strokeWidth={2.25} />
          )}
          {isOverdue && <AlertCircle size={16} color={colors.frost} strokeWidth={2.25} />}
          {state === STEP_STATE.Locked && (
            <Lock size={14} color={colors.textMuted} strokeWidth={2} />
          )}
          {state === STEP_STATE.Failed && <X size={16} color={colors.frost} strokeWidth={2.5} />}
        </View>
        {!isLast && <View style={[styles.connector, isDone && styles.connectorCompleted]} />}
      </View>

      <View style={styles.body}>
        <Text variant="bodyBold" color={state === STEP_STATE.Locked ? 'textMuted' : 'deepGreen'}>
          {name}
        </Text>

        {/*
          Provenance caption (Deal Steps v2). One quiet line under the name:
          who says this step is done, and when. The label is the server's;
          the client only picks the tone (green for the brand's word, muted
          for the user's own mark) and formats the time. Always visible on
          done steps, even collapsed, because it IS the information the step
          carries now.
        */}
        {hasPill && provenanceLabel && (
          <View style={[styles.metaPill, PILL_STYLE[state]]}>
            <View style={styles.metaPillIcon}>
              {isReported && <Clock size={11} strokeWidth={2.75} color={colors.warnPillText} />}
              {isOverdue && (
                <AlertCircle size={11} strokeWidth={2.75} color={colors.overduePillText} />
              )}
              {isDone && (
                <Check
                  size={11}
                  strokeWidth={3}
                  color={
                    state === STEP_STATE.Confirmed ? colors.paidPillText : colors.markedPillText
                  }
                />
              )}
            </View>
            <Text
              style={[styles.metaPillText, PILL_TEXT_STYLE[state]]}
              testID={testID ? `${testID}-provenance` : undefined}
            >
              {provenanceLabel}
              {timeSegment ? (
                <Text
                  style={[
                    styles.metaPillText,
                    PILL_TEXT_STYLE[state],
                    provenanceAtLabel ? styles.metaPillTime : null,
                  ]}
                >
                  {`\u00A0· ${timeSegment}`}
                </Text>
              ) : null}
            </Text>
          </View>
        )}

        {/* Caption size but charcoal, not muted: the instructions must read as
            clearly as the deal description above them, yet stay a step down
            from it so an expanded stepper doesn't become one flat block. */}
        {showDescription && (
          <Text variant="caption" color="text" style={styles.description}>
            {shownDescription}
          </Text>
        )}

        {showDescription && callout && (
          <View style={styles.callout} testID={testID ? `${testID}-callout` : undefined}>
            <AlertTriangle
              size={15}
              color={colors.text}
              strokeWidth={2.25}
              style={styles.calloutIcon}
            />
            <Text variant="caption" color="text" style={styles.calloutText}>
              {callout}
            </Text>
          </View>
        )}

        {/*
          Reference link as a quiet inline link on its OWN line. With `ctas`,
          this covers the non-active states only (on the active step an
          open_link cta becomes a button below). Without `ctas` (legacy /
          old server) it's the original inline reference link, on every state.
          Own line + vertical padding = a real tap target, clear of the
          collapse toggle's hitSlop below.
        */}
        {showDescription &&
          (hasCtas
            ? state !== STEP_STATE.Active &&
              ctas!
                .filter((cta) => cta.action === 'open_link' && cta.url)
                .map((cta, i) => (
                  <Text
                    key={i}
                    variant="caption"
                    link
                    onPress={() => openWebUrl(cta.url!)}
                    accessibilityLabel={`Open ${cta.label}`}
                    testID={testID ? `${testID}-link` : undefined}
                    style={styles.link}
                  >
                    {cta.label}
                  </Text>
                ))
            : linkUrl && (
                <Text
                  variant="caption"
                  link
                  onPress={() => openWebUrl(linkUrl)}
                  accessibilityLabel={`Open ${linkLabel ?? 'reference link'}`}
                  testID={testID ? `${testID}-link` : undefined}
                  style={styles.link}
                >
                  {linkLabel ?? 'Click here'}
                </Text>
              ))}

        {/* Authored waiting copy already says when it updates; the generic hint is for deals without one. */}
        {state === STEP_STATE.Waiting && !waitingDescription && (
          <Text variant="caption" color="textMuted" style={styles.waitingHint}>
            Updates automatically — usually within 24 hours of finishing.
          </Text>
        )}

        {/*
          Active-step actions. Data-driven when `ctas` is present: each cta is a
          full-width button — kind → variant (primary = filled green,
          secondary = outline), action → behavior (open_link opens its url,
          advance fires onCtaPress). This is the card's ask: the sign-up link is
          a button, not near-invisible caption text. An open_link cta with no
          resolvable url is skipped (never a dead button). Falls back to the
          legacy single primary CTA for un-migrated deals / older servers.
        */}
        {state === STEP_STATE.Active &&
          (hasCtas ? (
            <View style={styles.actions}>
              {ctas!.map((cta, i) => {
                const variant = cta.kind === 'primary' ? 'primary' : 'outline';
                const key = testID ? `${testID}-cta-${i}` : undefined;
                if (cta.action === 'open_link') {
                  if (!cta.url) return null;
                  const url = cta.url;
                  return (
                    <Button
                      key={i}
                      label={cta.label}
                      variant={variant}
                      size="base"
                      fullWidth
                      trailingIcon={ArrowUpRight}
                      onPress={() => openWebUrl(url)}
                      accessibilityLabel={`Open ${cta.label}`}
                      testID={key}
                    />
                  );
                }
                if (!onCtaPress) return null;
                return (
                  <Button
                    key={i}
                    label={cta.label}
                    variant={variant}
                    size="base"
                    fullWidth
                    onPress={() => onCtaPress(cta)}
                    loading={ctaLoading}
                    testID={key}
                  />
                );
              })}
            </View>
          ) : (
            ctaLabel &&
            onCtaPress && (
              <Button
                label={ctaLabel}
                variant="primary"
                size="base"
                onPress={() => onCtaPress()}
                loading={ctaLoading}
                style={styles.cta}
              />
            )
          ))}

        {!alwaysExpanded && (
          <Pressable
            onPress={() => setExpanded((prev) => !prev)}
            // top intentionally small: a larger top hitSlop reaches up into
            // the reference link that sits directly above and steals its taps.
            hitSlop={{ top: 4, bottom: 12, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={expanded ? 'Hide step details' : 'View step'}
            testID={testID ? `${testID}-toggle` : undefined}
          >
            <Text variant="captionBold" color="deepGreen" style={styles.toggle}>
              {expanded ? 'Hide step' : 'View step'}
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const STATE_LABEL: Record<StepState, string> = {
  [STEP_STATE.Completed]: 'Completed',
  [STEP_STATE.Confirmed]: 'Confirmed',
  [STEP_STATE.Reported]: "We're checking this",
  [STEP_STATE.Overdue]: 'Double-check this step',
  [STEP_STATE.Active]: 'In progress',
  [STEP_STATE.Waiting]: 'Processing',
  [STEP_STATE.Locked]: 'Locked',
  [STEP_STATE.Failed]: 'Failed',
};

// Active = hollow frost circle with deepGreen ring + deepGreen
// position number. NILClub-style: the user-actionable step reads
// distinctly from the Completed state (solid green + check) without
// fighting the CTA Button below for visual weight.
//
// Pending = warning-orange circle with frost clock icon. Communicates
// "server processing, no action needed" — distinct from Active where
// the user has work to do.
//
// Confirmed = Electric Green (the brand's "money moment" color) with the
// same frost check as Completed. The two done states share a glyph on
// purpose: same fact (done), different authority (you vs the brand), told by
// fill color plus the caption under the name.
const BULLET_STYLE: Record<StepState, ViewStyle> = {
  [STEP_STATE.Completed]: { backgroundColor: colors.deepGreen },
  [STEP_STATE.Confirmed]: { backgroundColor: colors.success },
  [STEP_STATE.Reported]: { backgroundColor: colors.warning },
  [STEP_STATE.Overdue]: { backgroundColor: colors.danger },
  [STEP_STATE.Active]: {
    backgroundColor: colors.frost,
    borderWidth: 2,
    borderColor: colors.deepGreen,
  },
  [STEP_STATE.Waiting]: { backgroundColor: colors.warning },
  [STEP_STATE.Locked]: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  [STEP_STATE.Failed]: { backgroundColor: colors.danger },
};

const BULLET_SIZE = 28;
const CONNECTOR_WIDTH = 2;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: spacing.base,
    paddingTop: spacing.sm,
  },
  bulletColumn: {
    width: BULLET_SIZE,
    alignItems: 'center',
  },
  bullet: {
    width: BULLET_SIZE,
    height: BULLET_SIZE,
    borderRadius: BULLET_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connector: {
    flex: 1,
    width: CONNECTOR_WIDTH,
    backgroundColor: colors.border,
    marginTop: spacing.xs / 2,
  },
  connectorCompleted: {
    backgroundColor: colors.deepGreen,
  },
  body: {
    flex: 1,
    paddingBottom: spacing.base,
    gap: spacing.xs,
  },
  description: {
    marginTop: spacing.xs,
  },
  // Provenance pill (Deal Steps v2): who says this step is done. Sentence
  // case at caption size, unlike the uppercase status Pill, because it
  // carries a step's worth of words. Green tint = the brand's postback,
  // blue tint = the user's own tap.
  // One bubble. Row so the icon sits beside the text; the text may wrap to a
  // second line (after the "·") and the bubble grows with it. flexShrink on
  // the text is what lets it wrap instead of pushing past the column.
  metaPill: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
    gap: spacing.xs + 1,
    marginTop: 2,
    paddingVertical: 3,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
  },
  // Pinned to the first text line (lineHeight 16, icon 11) so a wrapped
  // bubble does not float its icon between the lines.
  metaPillIcon: {
    marginTop: 2.5,
  },
  metaPillConfirmed: {
    backgroundColor: colors.paidPillBg,
  },
  metaPillMarked: {
    backgroundColor: colors.markedPillBg,
  },
  metaPillReported: {
    backgroundColor: colors.warnPillBg,
  },
  metaPillOverdue: {
    backgroundColor: colors.overduePillBg,
  },
  metaPillText: {
    flexShrink: 1,
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
  // "You marked it done 49m ago" in the regular weight: a footnote to the
  // status, not a second status.
  metaPillTime: {
    fontFamily: fonts.body,
  },
  metaPillTextConfirmed: {
    color: colors.paidPillText,
  },
  metaPillTextMarked: {
    color: colors.markedPillText,
  },
  metaPillTextReported: {
    color: colors.warnPillText,
  },
  metaPillTextOverdue: {
    color: colors.overduePillText,
  },
  // Warm-cream warning box. The same one-off pair the Refer tab's amber
  // pills use (colors.warnPillBg / warnPillBorder in theme.ts) — this is
  // now the only place the literals remain; NudgeCalloutCard, which shared
  // them, was deleted 2026-09-16.
  callout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.base,
    backgroundColor: '#FFF6E0',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E8C76A',
  },
  calloutIcon: {
    marginTop: 1,
  },
  calloutText: {
    flex: 1,
    lineHeight: fontSizes.sm * 1.4,
  },
  cta: {
    marginTop: spacing.sm,
    alignSelf: 'flex-start',
  },
  // Data-driven cta buttons: full-width, stacked, primary above secondary.
  actions: {
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  waitingHint: {
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  // Own-line reference link: left-aligned with vertical padding so it's a
  // real tap target, clearly separated from the collapse toggle below.
  link: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
  },
  toggle: {
    marginTop: spacing.sm,
  },
});

// Pill tint per state. Green = the brand's postback, amber = the user's own
// report still waiting, red = waiting too long. Blue is the retired legacy
// "completed" name, kept so a cached payload still renders.
const PILL_STYLE: Partial<Record<StepState, ViewStyle>> = {
  [STEP_STATE.Confirmed]: styles.metaPillConfirmed,
  [STEP_STATE.Completed]: styles.metaPillMarked,
  [STEP_STATE.Reported]: styles.metaPillReported,
  [STEP_STATE.Overdue]: styles.metaPillOverdue,
};
const PILL_TEXT_STYLE: Partial<Record<StepState, TextStyle>> = {
  [STEP_STATE.Confirmed]: styles.metaPillTextConfirmed,
  [STEP_STATE.Completed]: styles.metaPillTextMarked,
  [STEP_STATE.Reported]: styles.metaPillTextReported,
  [STEP_STATE.Overdue]: styles.metaPillTextOverdue,
};
